import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

const {registerModule} = window;

/**
 * FeatureList module
 * Takes a feature list HTML Element and
 * animates the feature media via gsap
 * @param {HTMLElement} element feature list section selector
 */
class FeatureList {
  constructor(element) {
    this.element = element;
    this.mount();
  }

  mount() {
    this.parentElement = this.element;
    if (this.parentElement === null || this.parentElement === undefined) return;

    this.checkMediaQuery();
    this.animateTimeline();
  }

  unmount() {
    this.element = null;
  }

  initialize() {
    this.bindUI();

    if (this.ui.sections.length === this.ui.media.length) {
      this.bindEvents();
    }
  }

  checkMediaQuery() {
    this.matchMedia = gsap.matchMedia();

    this.matchMedia.add({
      isDesktop: '(min-width: 1024px) and (prefers-reduced-motion: no-preference) and (orientation: landscape)',
    }, (context) => {
      if (context.conditions.isDesktop) {
        this.initialize();
      }
    });
  }

  bindUI() {
    this.ui = {};
    this.ui.sections = [...this.parentElement.querySelectorAll('.feature-container')];
    this.ui.media = [...this.parentElement.querySelectorAll('[data-element="feature-list-media"]')];
  }

  bindEvents() {
    if (window.location.hash && document.getElementById(window.location.hash.slice(1)) && !['reload', 'back_forward'].includes(window.performance?.getEntries()[0]?.type)) {
      this.unresolvedJump = true;
      this.jumpTarget = document.getElementById(window.location.hash.slice(1));
    }
    this.ui.media.forEach((media) => {
      gsap.set(media, {transformOrigin: 'center'});
    });

    this.ui.sections.forEach((feature) => {
      ScrollTrigger.create({
        trigger: feature,
        start: 'top 30%',
        end: 'bottom 80%',
        onEnter: () => this.enterAnimation(feature, 'down'),
        onEnterBack: () => this.enterAnimation(feature, 'up'),
        onRefresh: () => {
          if (!this.jumpTarget || !this.unresolvedJump || this.jumpTarget.getBoundingClientRect().top <= 0) {
            this.unresolvedJump = false;
          } else {
            this.jumpTarget.scrollIntoView({behavior: 'instant'});
          }
        },
      });
    });
  }

  animateTimeline() {
    gsap.utils.toArray(this.element.querySelectorAll('.feature__timeline-progress')).forEach((effect) => {
      const containerTrigger = effect.closest('.feature__timeline');
      gsap.to(effect, {
        scaleY: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: containerTrigger,
          start: 'top center',
          end: 'bottom center',
          scrub: 0,
          ease: 'none',
        },
      });
    });

    gsap.utils.toArray(this.element.querySelectorAll('.feature__timeline-ball')).forEach((effect) => {
      gsap.to(effect, {
        backgroundImage: 'radial-gradient(circle at top, var(--color-timeline-fill) 100%, var(--color-timeline-track) 100%)',
        ease: 'none',
        scrollTrigger: {
          trigger: effect,
          start: 'top center',
          end: 'bottom center',
          scrub: 0,
          ease: 'none',
        },
      });
    });
  }

  /**
   * EnterAnimation
   *
   * This function handles the opacity of the
   * feature image when the user scrolls into view
   *
   * @param {HTMLElement} selector - the feature container that holds all of the content
   * @param {string} dir - direction of the scroll
   */
  enterAnimation(selector, dir) {
    const direction = dir == 'up' ? -1 : 1;

    const speed = 0.6;
    const currentIndex = this.ui.sections.indexOf(selector);
    const translate = 24 * direction;

    // get active image in section
    let activeMedia = this.parentElement.querySelector('[data-element="feature-list-media"].is-active');
    let activeMediaIndex = this.ui.media.indexOf(activeMedia);
    activeMediaIndex = activeMediaIndex == -1 ? 0 : activeMediaIndex;

    // if the image for the current section is active, do nothing
    if (currentIndex != activeMediaIndex) {
      // otherwise, do all the animations and class changes
      const nextActiveMedia = this.ui.media[currentIndex];
      // transition in the new active media
      gsap.fromTo(
        nextActiveMedia,
        {autoAlpha: 0, y: translate},
        {duration: speed, autoAlpha: 1, y: 0},
      );
      nextActiveMedia.classList.add('is-active');

      // transition out the old active media
      activeMedia = this.ui.media[activeMediaIndex];
      gsap.to(activeMedia, {duration: speed, autoAlpha: 0, y: translate * -1});
      activeMedia.classList.remove('is-active');
    }
  }
}

registerModule('FeatureList', FeatureList);
